import PropTypes from 'prop-types'
import { useTranslation } from 'next-i18next'

// Context
import { useMatchLog } from '@/root/context/MatchContext'

// Internal components
import PlayerGraph from '@/components/PlayerGraph/PlayerGraph'
import { Fragment, useContext } from 'react'
import { UserContext } from '@/root/context/UserContext'

const PlayerList = (props) => {
  const {
    modifiers,
    playerList,
    type,
    clickablePlayers,
    view,
    selectedSpot,
    setSelectedSpot,
    setSelectedModalPlayer,
    newInitialLineUp,
    setNewInitialLineUp,
    totalPlayed,
    showPositions,
    editMode,
    players,
    editLineUp,
    setEditLineUp
  } = props

  const modifierClasses = modifiers ? ' ' + modifiers.join(' ') : ''
  const {
    dispatch,
    substitutes,
    currentLineUp,
    initialLineUp,
    setShowSidebar,
    sidebarTabNav,
    setSidebarTabNav,
    playerFormationRef,
    setSelectedPlayer,
    selectedPlayer,
    match,
    visible
  } = useMatchLog()

  const userContext = useContext(UserContext)
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // Team matches duration (based on age group)
  const matchDuration = match?.team?.[0]?.ageGroup?.[0]?.matchDuration

  /**
   * Handle player substitution
   *
   * @param player
   */
  const handleChangeSubstitute = (player) => {
    setShowSidebar(false)
    setSidebarTabNav('substitute')

    const newLineUp = currentLineUp.map((playerObj) => {
      return playerObj.uuid === selectedPlayer.uuid ? player : playerObj
    })

    // creating new substitutes,
    const newSubstitutes = substitutes.map((playerObj) => {
      // finding player in initial line up and replacing it with player that gets in
      const playerFromLineup = initialLineUp.find((o) => o.uuid === selectedPlayer.uuid)
      return playerObj.uuid === player.uuid ? playerFromLineup : playerObj
    })

    const currentFormation = playerFormationRef.querySelectorAll('.Player')
    const oldPlayerIndex = currentLineUp.findIndex((oldPlayer) => oldPlayer.uuid === selectedPlayer.uuid)
    const oldPlayerEl = currentFormation[oldPlayerIndex]
    oldPlayerEl.style.transform = 'scale(1.2)'

    setTimeout(() => {
      oldPlayerEl.style.transform = 'scale(1)'
      dispatch({ type: 'MATCH_UPDATE_LINEUP', currentLineUp: newLineUp })
      dispatch({ type: 'MATCH_UPDATE_SUBSTITUTES', substitutes: newSubstitutes })
      setSelectedPlayer(null)
    }, 1000)
  }

  /**
   * Change player position
   *
   * @param player
   */
  const handleChangePosition = (player) => {
    const newLineUp = currentLineUp.map((playerObj) => {
      return playerObj.uuid === selectedPlayer.uuid
        ? player
        : playerObj && playerObj.uuid === player.uuid
        ? selectedPlayer
        : playerObj
    })

    const oldPlayerIndex = currentLineUp.findIndex((oldPlayer) => oldPlayer.uuid === selectedPlayer.uuid)
    const newPlayerIndex = currentLineUp.findIndex((newPlayer) => newPlayer.uuid === player.uuid)
    const currentFormation = playerFormationRef.querySelectorAll('.Player')
    const oldPlayerEl = currentFormation[oldPlayerIndex]
    const newPlayerEl = currentFormation[newPlayerIndex]

    oldPlayerEl.style.transform = 'scale(1.2)'
    newPlayerEl.style.transform = 'scale(1.2)'

    setTimeout(() => {
      oldPlayerEl.style.transform = 'scale(1)'
      newPlayerEl.style.transform = 'scale(1)'

      dispatch({ type: 'MATCH_UPDATE_LINEUP', currentLineUp: newLineUp })
      setShowSidebar(false)
      setSidebarTabNav('substitute')
      setSelectedPlayer(null)
    }, 1000)
  }

  const handleSetLineUp = (player) => {
    const updatedLineUp = [...newInitialLineUp].map((lineUpPlayer, index) => {
      if (lineUpPlayer.uuid === player.uuid && index === selectedSpot) {
        return player
      }

      // Check if player is already in field, replace if so
      if (lineUpPlayer.uuid === player.uuid && lineUpPlayer.uuid !== '' && player.uuid !== '') {
        return { name: '', uuid: '', icon: 'add' }
      }

      // Set player shirt to no icon, but keep selectable
      if (index === selectedSpot) {
        player.icon = 'change'
        return player
      }

      return lineUpPlayer
    })

    setNewInitialLineUp(updatedLineUp)
    setSelectedModalPlayer(null)
    setSelectedSpot(null)
  }

  const handleClick = (player) => {
    if (editMode) return
    if (clickablePlayers === 'modal') {
      return handleSetLineUp(player)
    } else {
      return clickablePlayers
        ? sidebarTabNav === 'substitute'
          ? handleChangeSubstitute(player)
          : handleChangePosition(player)
        : null
    }
  }

  const updatePlayerPlayingTime = (e, player) => {
    // Hard validation to check if value is not lower than 0 and not higher than matchduration
    if (e.currentTarget.value > matchDuration) {
      e.currentTarget.value = matchDuration
    }
    if (e.currentTarget.value < 0) {
      e.currentTarget.value = 0
    }

    const newPlayingTime = parseInt(e.target.value)
    const newPlayer = { ...player }
    const items = editLineUp.length ? editLineUp : players

    newPlayer.playingTime = newPlayingTime
    const newLineUp = Array.from(items).map((playerObj) => {
      if (playerObj.uuid === player.uuid) {
        return newPlayer
      } else {
        return playerObj
      }
    })

    setEditLineUp(newLineUp)
  }

  return (
    <div className={`PlayerList${modifierClasses}`}>
      {playerList?.map((list, index) => {
        return (
          <ul className="PlayerList-list" key={`List${index}`}>
            {list?.map((player, index) => {
              if (selectedPlayer && player.uuid === selectedPlayer.uuid) {
                return <Fragment key={player.uuid}></Fragment>
              }
              return (
                <li className="PlayerList-player" onClick={() => handleClick(player)} key={player.uuid}>
                  <span className={`PlayerList-playerName ${player?.inField ? 'PlayerList-playerNameSet' : ''}`}>
                    {/* TODO pass formation data */}
                    {showPositions && <span className="PlayerList-playerPosition">{player?.position}</span>}
                    {player.name}
                  </span>
                  {type === 'playingTime' && (
                    <>
                      {editMode ? (
                        <span className="PlayerList-playerTime has-padding">
                          <input
                            type="number"
                            defaultValue={player.playingTime}
                            min="0"
                            max={matchDuration}
                            name={`lineUpPlayer__${index}`}
                            className="PlayerList-edit"
                            onChange={(e) => updatePlayerPlayingTime(e, player)}
                          />
                        </span>
                      ) : (
                        <span className="PlayerList-playerTime">
                          {/* Divide seconds to minutes in matchlog */}
                          {visible ? Math.round(player.playingTime / 60) : player.playingTime}
                        </span>
                      )}
                    </>
                  )}

                  {player.inField && <span className="PlayerList-inField"> {t('MDL__TXT_DRAFTED')} </span>}
                  {type === 'graph' && (
                    <PlayerGraph totalPlayed={totalPlayed} type="minutes" player={player} view={view} />
                  )}
                </li>
              )
            })}
          </ul>
        )
      })}
    </div>
  )
}

PlayerList.defaultPropTypes = {
  modifiers: [],
  type: '',
  playerList: [],
  clickablePlayers: false,
  view: 'minutes',
  selectedSpot: 0,
  setSelectedSpot: () => {},
  setSelectedModalPlayer: () => {},
  newInitialLineUp: [],
  setNewInitialLineUp: () => {},
  totalPlayed: 0,
  showPositions: false,
  editMode: false,
  players: [],
  setEditLineUp: () => {}
}

PlayerList.propTypes = {
  modifiers: PropTypes.array,
  type: PropTypes.string,
  playerList: PropTypes.array,
  clickablePlayers: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  view: PropTypes.string,
  selectedSpot: PropTypes.number,
  setSelectedSpot: PropTypes.func,
  setSelectedModalPlayer: PropTypes.func,
  newInitialLineUp: PropTypes.array,
  setNewInitialLineUp: PropTypes.func,
  totalPlayed: PropTypes.number,
  showPositions: PropTypes.bool,
  editMode: PropTypes.bool,
  players: PropTypes.array,
  editLineUp: PropTypes.array,
  setEditLineUp: PropTypes.func
}

export default PlayerList
